import { Button } from '@dataroom/shared';
import type { GrantWarning } from '@dataroom/shared';

interface GrantWarningListProps {
  /** The advisory warnings to display (never rendered when empty). */
  warnings: GrantWarning[];
  /** Whether the acknowledged re-submit is in flight. */
  pending: boolean;
  /** Proceed past the warnings (re-sends with acknowledgeWarnings=true). */
  onProceed: () => void;
  /** Dismiss without proceeding. */
  onCancel: () => void;
}

/**
 * The advise + acknowledge surface (ADR-0010 §3). Warnings are ADVISORY, not
 * errors: they are shown in an alert region with a "Proceed anyway" that
 * re-submits the action acknowledged. Presentational — the page owns the
 * pending action and the acknowledge re-send.
 */
export function GrantWarningList({
  warnings,
  pending,
  onProceed,
  onCancel,
}: GrantWarningListProps) {
  if (warnings.length === 0) return null;

  return (
    <div role="alert" className="rounded-md border border-st-check-line bg-st-check p-3 text-st-check-fg">
      <p className="text-sm font-medium">
        {warnings.length === 1 ? 'Advisory warning' : `${warnings.length} advisory warnings`}
      </p>
      <ul className="mt-1.5 list-disc space-y-1 pl-5 text-sm">
        {warnings.map((w) => (
          <li key={w.code}>{w.message}</li>
        ))}
      </ul>
      <div className="mt-3 flex gap-2">
        <Button size="sm" onClick={onProceed} disabled={pending}>
          {pending ? 'Proceeding…' : 'Proceed anyway'}
        </Button>
        <Button variant="outline" size="sm" onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
